/**
 * useDirections hook.
 *
 * Manages the "Explore directions" pipeline: fetches tag direction suggestions
 * from POST /api/deck/directions and caches them per commander per session.
 *
 * The `explore()` function always triggers a fresh fetch (per PRD Section 2:
 * "clicking re-runs"). The cache is used for persistence across tab switches
 * so results are visible without re-fetching after switching tabs.
 */

import { useCallback } from 'react';
import type { DirectionSuggestion } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../stores/deck-store.js';
import { api, ApiClientError } from '../lib/api-client.js';

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseDirectionsResult {
  suggestions: DirectionSuggestion[] | null;
  isLoading: boolean;
  error: string | null;
  canExplore: boolean;
  explore: () => Promise<void>;
}

/**
 * Hook for the "Explore directions" feature.
 *
 * Reads the deck's commander(s) and current tag profile from the store.
 * Exposes an `explore()` function that triggers the server-side pipeline.
 * Results are cached per commander key to survive tab switches.
 *
 * @returns Hook state and explore callback
 */
export function useDirections(): UseDirectionsResult {
  const entries = useDeckStore((s) => s.entries);
  const tagProfile = useDeckStore((s) => s.tagProfile);
  const directionsCache = useDeckStore((s) => s.directionsCache);
  const isDirectionsLoading = useDeckStore((s) => s.isDirectionsLoading);
  const directionsError = useDeckStore((s) => s.directionsError);
  const setDirectionsResult = useDeckStore((s) => s.setDirectionsResult);
  const setDirectionsLoading = useDeckStore((s) => s.setDirectionsLoading);
  const setDirectionsError = useDeckStore((s) => s.setDirectionsError);

  // Compute commander entries and cache key
  const commanderEntries = entries.filter((e) => e.isCommander);
  const canExplore = commanderEntries.length > 0;

  // Cache key: raw commander names joined with "::" (normalization is server-side)
  const cacheKey = commanderEntries.map((e) => e.card.name).join('::');

  // Read cached suggestions (null if no cache hit)
  const suggestions = canExplore ? (directionsCache.get(cacheKey) ?? null) : null;

  // Build deck tag counts for server-side threshold filtering
  const deckTagCounts =
    tagProfile?.entries.map((e) => ({ slug: e.tag.slug, matchCount: e.matchCount })) ?? [];

  const explore = useCallback(async (): Promise<void> => {
    // Read latest state directly from store to avoid stale closures
    const state = useDeckStore.getState();
    const cmdEntries = state.entries.filter((e) => e.isCommander);
    if (cmdEntries.length === 0) return;

    const commanderNames = cmdEntries.map((e) => e.card.name);
    const key = commanderNames.join('::');
    const tagCounts =
      state.tagProfile?.entries.map((e) => ({ slug: e.tag.slug, matchCount: e.matchCount })) ?? [];

    state.setDirectionsError(null);

    try {
      const response = await api.fetchDirections(commanderNames, tagCounts);
      state.setDirectionsResult(key, response.suggestions);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? 'Could not load suggestions. EDHREC or Tagger may be unavailable.'
          : 'Could not load suggestions. EDHREC or Tagger may be unavailable.';
      state.setDirectionsError(message);
    }
    // Note: setDirectionsLoading(false) is called inside setDirectionsResult/setDirectionsError
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    suggestions,
    isLoading: isDirectionsLoading,
    error: directionsError,
    canExplore,
    explore,
  };
}
